// One in-app confirmation for the whole application, instead of
// window.confirm. A native dialog looks foreign (no Xentros styling, browser
// buttons in the browser's language) and blocks the renderer of the tab until
// it is answered — during the group audit an unfocused tab hung behind one.
//
// Usage: `if (!(await confirmDialog(text))) return;` — the text is the same
// (already translated) question the native dialog carried; a "\n" starts a new
// paragraph. <ConfirmDialogHost /> (ConfirmDialogHost.tsx) is mounted once in
// App.tsx and renders the queue's head.

export type PendingConfirm = { id: number; message: string; resolve: (confirmed: boolean) => void };

let queue: PendingConfirm[] = [];
let nextId = 1;
let mountedHosts = 0;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

export function subscribeConfirmQueue(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function currentConfirmRequest() {
  return queue[0] ?? null;
}

/** Asks `message` in the in-app dialog; resolves true on Confirm, false on Cancel or Escape. */
export function confirmDialog(message: string): Promise<boolean> {
  // No host (a test render or a page outside App): never hang the caller.
  if (mountedHosts === 0) return Promise.resolve(false);
  return new Promise<boolean>((resolve) => {
    queue = [...queue, { id: nextId++, message, resolve }];
    emit();
  });
}

/** Answers the dialog on screen. */
export function settleConfirmRequest(confirmed: boolean) {
  const [head, ...rest] = queue;
  if (!head) return;
  queue = rest;
  emit();
  head.resolve(confirmed);
}

export function registerConfirmHost() {
  mountedHosts += 1;
  return () => {
    mountedHosts -= 1;
    // A host going away must not leave callers waiting forever.
    if (mountedHosts === 0) while (queue.length) settleConfirmRequest(false);
  };
}
